import { getCode, getData } from 'country-list';

const countryCodesByNormalizedName = new Map(
  getData().map(({ code, name }) => [
    name.replace(/\s+\(the\)$/i, '').toLowerCase(),
    code,
  ]),
);

/**
 * Normalize a country name or alpha-2 code for API country fields.
 *
 * Some country datasets use formal names with a trailing "(the)", while
 * country-list uses the same names without that suffix. Keep unknown values
 * unchanged so legacy data is not silently discarded.
 */
export const toCountryCode = value => {
  if (!value || typeof value !== 'string') return value || null;

  const country = value.trim();
  if (country.length === 2) return country.toUpperCase();

  const nameWithoutArticle = country.replace(/\s+\(the\)$/i, '');
  return (
    getCode(country) ||
    countryCodesByNormalizedName.get(nameWithoutArticle.toLowerCase()) ||
    country
  );
};

/**
 * Normalize country-bearing fields without mutating the original payload.
 */
export const normalizeCountryFields = (data, fields = ['country']) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;

  return fields.reduce((normalized, field) => {
    if (!normalized?.[field]) return normalized;

    const countryCode = toCountryCode(normalized[field]);
    if (countryCode === normalized[field]) return normalized;

    return {
      ...normalized,
      [field]: countryCode,
    };
  }, data);
};

/**
 * Normalize the nested country fields supported by bank-account payloads.
 */
export const normalizeBankAccountCountries = data => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;

  const branchAddress = normalizeCountryFields(data.branch_address);
  const ownerAddress = normalizeCountryFields(data.owner?.address);

  if (
    branchAddress === data.branch_address &&
    ownerAddress === data.owner?.address
  ) {
    return data;
  }

  return {
    ...data,
    ...(branchAddress !== data.branch_address && {
      branch_address: branchAddress,
    }),
    ...(ownerAddress !== data.owner?.address && {
      owner: {
        ...data.owner,
        address: ownerAddress,
      },
    }),
  };
};
